import { useMemo } from "react";
import { useMediaQuery } from "@fox1ck-org/ui";
import type { ReportViewDefinition } from "@gen/tracker/v1/reporting_pb";

import { useReportParams, type ReportParams } from "@/lib/hooks/use-report-params";
import { MAX_LEVELS, formatFilters, parseFilters, type DimFilter, type ReportFilters } from "@/lib/report";
import type { ConversionBasis } from "@/lib/period";
import { PRESET_SETS } from "@/lib/campaigns-table";
import { REPORT_MONEY_NARROW } from "./columns";

/**
 * The report's own address, beside the Top bar's period (`useReportParams`):
 *
 *   dims=campaign,creative,country — the levels, outermost first
 *   metrics=clicks,leads,…         — the columns, when they are no set's
 *   cset=money | <saved set id>    — the column set the columns came from
 *   flt=d:country:in:RO,PL;m:profit:lt:0 — dimension filters and metric conditions ("-" = none)
 *   rs=profit:desc                 — the server's sort
 *   cmpv=side                      — «Рядом»: a column per period; default «Только Δ» under each value
 *   ltype=prelanding               — «Ленды»: only this kind of page
 *
 * Each key overrides the opened view's definition, which overrides the
 * engine's defaults; a key that is absent says «as the view has it». The same
 * keys are read on «Отчёты», on a campaign's «Отчёт» tab and on a lander's
 * «Цифры», so «Открыть в Отчётах» is the same query with the pin as a filter.
 */

/**
 * «Деньги» at ≤1440 (the rail at 56, boards Camp1280): the bots' share, LP
 * CTR and CPA step aside so the tree, the money and ROI fit without the table
 * running off the right edge. Another set, or one's own columns, is shown as
 * chosen. Pure.
 */
export function moneyAt(narrow: boolean, keys: readonly string[]): readonly string[] {
  return narrow && keys === PRESET_SETS.money ? REPORT_MONEY_NARROW : keys;
}

export const REPORT_KEYS = ["dims", "metrics", "cset", "flt", "rs", "cmpv", "ltype"] as const;

export interface ReportDefaults {
  dims: string[];
  /** A column set id; its keys are the columns. Default «Деньги». */
  set?: string;
  /** Columns that are no set's — they win over `set`. */
  metrics?: string[];
  sort?: { key: string; desc: boolean };
}

export type CompareView = "delta" | "side";

export interface ReportState {
  params: ReportParams;
  dims: string[];
  /** The column set's id, or "" when the columns are the reader's own. */
  set: string;
  metrics: string[];
  /** The person's filters — without the engine's pinned ones. */
  filters: ReportFilters;
  /** What the server is asked: the pinned filters and the person's. */
  query: ReportFilters;
  sort: { key: string; desc: boolean };
  basis: ConversionBasis;
  compareView: CompareView;
  landerType: string;
  /** Something in the address differs from the opened view. */
  dirty: boolean;
  setDims: (dims: string[]) => void;
  setColumns: (next: { set: string; metrics: string[] }) => void;
  setFilters: (f: ReportFilters) => void;
  setSort: (s: { key: string; desc: boolean } | null) => void;
  setCompareView: (v: CompareView) => void;
  setLanderType: (v: string) => void;
}

/** The view a definition reopens, in the engine's words. Pure. */
export function definitionBase(d: ReportViewDefinition | undefined): Partial<ReportDefaults> & { filters?: ReportFilters } {
  if (!d) return {};
  return {
    dims: d.dimensionKeys.length ? d.dimensionKeys : undefined,
    metrics: d.metricKeys.length ? d.metricKeys : undefined,
    sort: d.sortMetricKey ? { key: d.sortMetricKey, desc: d.sortDesc } : undefined,
    filters: {
      dims: d.dimensionFilters.map((f) => ({ key: f.dimensionKey, op: f.op, values: [...f.values] })),
      metrics: d.metricFilters.map((m) => ({ key: m.metricKey, op: m.op, value: m.value })),
    },
  };
}

/** `dims=a,b` → known keys, unique, at most four; the fallback when nothing is left. Pure. */
export function parseDims(raw: string, known: ReadonlySet<string>, fallback: string[]): string[] {
  const dims = [...new Set(raw.split(",").map((d) => d.trim()).filter((d) => d && (known.size === 0 || known.has(d))))].slice(0, MAX_LEVELS);
  return dims.length ? dims : fallback;
}

/** Filters that pin the engine to one object stay out of the person's chips, and out of the address. Pure. */
export function mergeFilters(pinned: readonly DimFilter[], own: ReportFilters): ReportFilters {
  return { dims: [...pinned, ...own.dims], metrics: own.metrics };
}

export function useReportState({
  defaults,
  base,
  pinned = [],
  knownDims,
}: {
  defaults: ReportDefaults;
  /** The opened saved view's definition, overriding the defaults. */
  base?: ReportViewDefinition;
  pinned?: readonly DimFilter[];
  knownDims: ReadonlySet<string>;
}): ReportState {
  const params = useReportParams();
  const narrow = useMediaQuery("(max-width: 1440px)");
  const from = definitionBase(base);
  const g = params.getFilter;

  const dimsRaw = g("dims");
  const metricsRaw = g("metrics");
  const setRaw = g("cset");
  const fltRaw = g("flt");
  const rsRaw = g("rs");

  const baseDims = from.dims ?? defaults.dims;
  const dims = dimsRaw ? parseDims(dimsRaw, knownDims, baseDims) : baseDims;

  const set = metricsRaw ? setRaw : setRaw || (from.metrics ? "" : defaults.metrics ? "" : (defaults.set ?? "money"));
  const metrics = metricsRaw
    ? metricsRaw.split(",").filter(Boolean)
    : (moneyAt(
        narrow,
        setRaw && setRaw in PRESET_SETS
          ? PRESET_SETS[setRaw as keyof typeof PRESET_SETS]
          : (from.metrics ?? defaults.metrics ?? PRESET_SETS[(defaults.set ?? "money") as keyof typeof PRESET_SETS] ?? PRESET_SETS.money),
      ) as string[]);

  const filters = useMemo(() => (fltRaw !== "" ? parseFilters(fltRaw) : (from.filters ?? { dims: [], metrics: [] })), [fltRaw, from.filters]);
  const query = useMemo(() => mergeFilters(pinned, filters), [pinned, filters]);

  const sort = rsRaw
    ? { key: rsRaw.split(":")[0]!, desc: rsRaw.split(":")[1] !== "asc" }
    : (from.sort ?? defaults.sort ?? { key: "profit", desc: true });

  return {
    params,
    dims,
    set,
    metrics,
    filters,
    query,
    sort,
    basis: params.basis,
    compareView: g("cmpv") === "side" ? "side" : "delta",
    landerType: g("ltype"),
    dirty: !!(dimsRaw || metricsRaw || setRaw || fltRaw || rsRaw),
    setDims: (next) => params.setFilters({ dims: next.join(",") || null }),
    // A set's columns are the set: the address keeps the id alone.
    setColumns: ({ set: s, metrics: m }) => params.setFilters({ cset: s || null, metrics: s && s in PRESET_SETS ? null : m.join(",") || null }),
    setFilters: (f) => params.setFilter("flt", formatFilters(f) || "-"),
    setSort: (s) => params.setFilter("rs", s ? `${s.key}:${s.desc ? "desc" : "asc"}` : null),
    setCompareView: (v) => params.setFilter("cmpv", v === "side" ? "side" : null),
    setLanderType: (v) => params.setFilter("ltype", v || null),
  };
}
